import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useAuth } from "../services/authProvider";

const MANAGE_POSITIONS = [3, 4, 8, 9, 99];
const CATEGORIES = ["ทั่วไป", "แบบฟอร์ม", "ระเบียบ/ประกาศ", "รายชื่อนักเรียน", "คู่มือ", "อื่นๆ"];

// ฐาน URL ของไฟล์ที่อัปโหลด (ตัด /api ออกจาก REACT_APP_API)
const FILE_BASE = process.env.REACT_APP_API?.replace(/\/api$/, "") || "";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" }) : "-");

const EXPIRE_PRESETS = [
  { value: "none", label: "ไม่หมดอายุ (เก็บถาวร)" },
  { value: "7", label: "7 วัน" },
  { value: "30", label: "30 วัน" },
  { value: "90", label: "90 วัน" },
  { value: "custom", label: "กำหนดวันเอง" },
];

// แปลงตัวเลือกวันหมดอายุ -> สตริง 'YYYY-MM-DD 23:59:59' (เวลาท้องถิ่น) หรือ null = เก็บถาวร
const resolveExpire = (mode, customDate) => {
  if (mode === "none") return null;
  let target;
  if (mode === "custom") {
    if (!customDate) return null;
    target = new Date(`${customDate}T23:59:59`);
  } else {
    target = new Date();
    target.setDate(target.getDate() + parseInt(mode, 10));
    target.setHours(23, 59, 59, 0);
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())} ${pad(target.getHours())}:${pad(target.getMinutes())}:${pad(target.getSeconds())}`;
};

export default function Documents() {
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const canManage = (posIds || []).some((p) => MANAGE_POSITIONS.includes(p));

  ///// Skin Switch /////
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ doc_title: "", doc_category: "ทั่วไป", doc_description: "" });
  const [file, setFile] = useState(null);
  const [expireMode, setExpireMode] = useState("none");
  const [expireDate, setExpireDate] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const loadDocuments = () => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getDocuments`)
      .then((res) => setDocuments(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        Swal.fire("ผิดพลาด", "ไม่สามารถโหลดรายการเอกสารได้", "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDocuments(); }, []);

  const openModal = () => {
    setForm({ doc_title: "", doc_category: "ทั่วไป", doc_description: "" });
    setFile(null);
    setExpireMode("none");
    setExpireDate("");
    setShowModal(true);
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!form.doc_title.trim()) return Swal.fire("แจ้งเตือน", "กรุณาระบุชื่อเอกสาร", "warning");
    if (!file) return Swal.fire("แจ้งเตือน", "กรุณาเลือกไฟล์", "warning");
    if (expireMode === "custom" && !expireDate) return Swal.fire("แจ้งเตือน", "กรุณาเลือกวันหมดอายุ", "warning");

    const doc_expire_at = resolveExpire(expireMode, expireDate);

    const fd = new FormData();
    fd.append("document", file);
    fd.append("doc_title", form.doc_title.trim());
    fd.append("doc_category", form.doc_category);
    fd.append("doc_description", form.doc_description);
    if (doc_expire_at) fd.append("doc_expire_at", doc_expire_at);

    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/uploadDocument`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(() => {
        setShowModal(false);
        Swal.fire("สำเร็จ", "อัปโหลดเอกสารเรียบร้อย", "success");
        loadDocuments();
      })
      .catch((err) => {
        console.error(err);
        Swal.fire("ผิดพลาด", err.response?.data?.message || "อัปโหลดไม่สำเร็จ", "error");
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = (doc) => {
    Swal.fire({
      title: "ยืนยันการลบ", text: `ลบเอกสาร "${doc.doc_title}" ?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก", confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.delete(`${process.env.REACT_APP_API}/deleteDocument/${doc.doc_id}`)
        .then(() => { Swal.fire("ลบแล้ว", "ลบเอกสารเรียบร้อย", "success"); loadDocuments(); })
        .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "ลบไม่สำเร็จ", "error"); })
        .finally(() => setLoading(false));
    });
  };

  // กรองตามหมวดที่เลือก แล้วจัดกลุ่มตามหมวด
  const filteredDocs = filterCategory === "all"
    ? documents
    : documents.filter((d) => (d.doc_category || "ทั่วไป") === filterCategory);

  const grouped = filteredDocs.reduce((acc, d) => {
    const cat = d.doc_category || "ทั่วไป";
    (acc[cat] = acc[cat] || []).push(d);
    return acc;
  }, {});

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทั่วไป</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ดาวน์โหลดเอกสาร</li>
            </ol>
            <h4 className="main-title mb-0">ดาวน์โหลดเอกสาร</h4>
          </div>
          {canManage && (
            <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openModal}>
              <i className="ri-upload-2-line fs-18 lh-1"></i> อัปโหลดเอกสาร
            </Button>
          )}
        </div>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <Card.Title as="h6" className="mb-0">รายการเอกสาร</Card.Title>
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-filter-3-line text-secondary"></i>
                  <Form.Select size="sm" style={{ width: "auto" }} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="all">ทุกหมวดหมู่</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                {filteredDocs.length === 0 ? (
                  <div className="text-center text-secondary py-5">
                    {documents.length === 0 ? "ยังไม่มีเอกสารในระบบ" : "ไม่พบเอกสารในหมวดหมู่นี้"}
                  </div>
                ) : (
                  Object.keys(grouped).map((cat) => (
                    <div key={cat} className="mb-4">
                      <h6 className="mb-2"><Badge bg="secondary" className="me-2">{cat}</Badge></h6>
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th>ชื่อเอกสาร</th>
                            <th style={{ width: "120px" }} className="text-center">ขนาด</th>
                            <th style={{ width: "140px" }} className="text-center">วันที่</th>
                            <th style={{ width: "150px" }} className="text-center">หมดอายุ</th>
                            <th style={{ width: "160px" }} className="text-center">จัดการ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grouped[cat].map((d) => (
                            <tr key={d.doc_id}>
                              <td>
                                <div className="fw-medium">{d.doc_title}</div>
                                {d.doc_description && <small className="text-secondary">{d.doc_description}</small>}
                              </td>
                              <td className="text-center">{formatSize(d.doc_filesize)}</td>
                              <td className="text-center">{formatDate(d.doc_created_at)}</td>
                              <td className="text-center">
                                {d.doc_expire_at
                                  ? <Badge bg="warning" text="dark"><i className="ri-time-line"></i> {formatDate(d.doc_expire_at)}</Badge>
                                  : <span className="text-secondary">ถาวร</span>}
                              </td>
                              <td className="text-center">
                                <a
                                  href={`${FILE_BASE}/uploads/documents/${d.doc_filename}`}
                                  target="_blank" rel="noreferrer"
                                  className="btn btn-sm btn-outline-primary me-1"
                                  download={d.doc_original_name || true}
                                >
                                  <i className="ri-download-2-line"></i> ดาวน์โหลด
                                </a>
                                {canManage && (
                                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(d)}>
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleUpload}>
          <Modal.Header closeButton>
            <Modal.Title as="h6">อัปโหลดเอกสาร</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อเอกสาร <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={form.doc_title} onChange={(e) => setForm({ ...form, doc_title: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>หมวดหมู่</Form.Label>
              <Form.Select value={form.doc_category} onChange={(e) => setForm({ ...form, doc_category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>คำอธิบาย</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.doc_description} onChange={(e) => setForm({ ...form, doc_description: e.target.value })} />
            </Form.Group>
            <Row>
              <Col xs={expireMode === "custom" ? 6 : 12}>
                <Form.Group className="mb-3">
                  <Form.Label>วันหมดอายุ</Form.Label>
                  <Form.Select value={expireMode} onChange={(e) => setExpireMode(e.target.value)}>
                    {EXPIRE_PRESETS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              {expireMode === "custom" && (
                <Col xs={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>เลือกวันที่ <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="date" value={expireDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExpireDate(e.target.value)} />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Form.Group className="mb-1">
              <Form.Label>ไฟล์ <span className="text-danger">*</span></Form.Label>
              <Form.Control type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp" onChange={(e) => setFile(e.target.files[0])} />
              <Form.Text className="text-secondary">รองรับ PDF, Word, Excel, PowerPoint, รูปภาพ — ไม่เกิน 10MB</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" type="submit">อัปโหลด</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
}
